import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, User, Flower2, Shield } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import Button from '../common/Button';

const Header = () => {
  const { user, logout, isAuthenticated } = useContext(AuthContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { to: '/obituaries/create', label: '부고 작성' },
    { to: '/obituaries', label: '부고 찾기' },
    { to: '/wreaths', label: '화환 보내기' },
    { to: '/support', label: '고객지원' }
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* 로고 */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-charcoal rounded-lg flex items-center justify-center shadow-md">
              <Flower2 className="text-ivory" size={24} strokeWidth={2} />
            </div>
            <span className="text-xl font-bold text-charcoal hidden sm:inline">
              모바일 장례 서비스
            </span>
            <span className="text-xl font-bold text-charcoal sm:hidden">
              장례 서비스
            </span>
          </Link>

          {/* 데스크톱 네비게이션 */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-gray-700 hover:text-charcoal font-medium transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin/dashboard"
              className="flex items-center space-x-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 transform hover:scale-105"
            >
              <Shield size={18} strokeWidth={2.5} />
              <span>관리자</span>
            </Link>
          </nav>

          {/* 데스크톱 사용자 메뉴 */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated() ? (
              <>
                <Link
                  to="/mypage"
                  className="flex items-center space-x-1 text-gray-700 hover:text-charcoal"
                >
                  <User size={20} />
                  <span>{user?.name || '마이페이지'}</span>
                </Link>
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  로그아웃
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline" size="sm">
                    로그인
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="primary" size="sm">
                    회원가입
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* 모바일 메뉴 버튼 */}
          <button
            className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            onClick={toggleMobileMenu}
            aria-label="메뉴 열기"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* 모바일 메뉴 */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 space-y-3">
            {/* 모바일 네비게이션 링크 */}
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            {/* 모바일 관리자 버튼 */}
            <Link
              to="/admin/dashboard"
              className="mx-4 flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg active:from-indigo-700 active:to-indigo-800 transition-all"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Shield size={20} strokeWidth={2.5} />
              <span>관리자</span>
            </Link>

            {/* 모바일 사용자 메뉴 */}
            <div className="border-t border-gray-200 pt-4 px-4 space-y-3">
              {isAuthenticated() ? (
                <>
                  <Link
                    to="/mypage"
                    className="flex items-center space-x-2 text-gray-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User size={20} />
                    <span>{user?.name || '마이페이지'}</span>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="w-full"
                  >
                    로그아웃
                  </Button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button variant="outline" size="sm" className="w-full">
                      로그인
                    </Button>
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button variant="primary" size="sm" className="w-full">
                      회원가입
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
